import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { config } from '@/core/config'
import { useAuthStore } from '@/store/auth.store'
import { 
  Employee, 
  ApiResponse, 
  EmployeeListResponse,
  GenderLookup,
  DepartmentLookup,
  CountryLookup,
  CityLookup,
  ProfessionIqamaLookup,
  NoticePeriodLookup,
  MaritalStatusLookup,
  JobLookup,
  StatusLookup,
} from '@/api/types'

/**
 * Employee management service
 * Handles all employee-related API operations with proper typing
 */
/**
 * Some backends persist the row then return HTTP 500 (e.g. secondary step throws).
 * If the JSON still indicates a successful save, treat as OK so the UI can show success.
 */
export function employeeSaveResponseLooksSuccessful(body: unknown): boolean {
  if (!body || typeof body !== 'object') return false
  const b = body as Record<string, unknown>
  if (b.success === false || b.Success === false) return false
  if (b.success === true || b.Success === true) return true

  const result = (b.result ?? b.Result) as Record<string, unknown> | undefined
  if (result) {
    if (result.isError === true || result.IsError === true) return false
    if (result.isError === false || result.IsError === false) return true
  }

  const id =
    b.id ??
    (b.data as Record<string, unknown> | undefined)?.id ??
    (b.Data as Record<string, unknown> | undefined)?.id
  if (id != null) {
    const n = Number(id)
    if (Number.isFinite(n) && n > 0) return true
  }

  return false
}

/** Serialize FormData for console debugging (multipart fields + file metadata). */
function formDataToDebugRecord(formData: FormData): Record<string, string> {
  const out: Record<string, string> = {}
  formData.forEach((v, k) => {
    out[k] = v instanceof File ? `[File: ${v.name}, ${v.size} bytes, ${v.type || 'no type'}]` : String(v)
  })
  return out
}

export class EmployeesService {
  /**
   * Get paginated list of employees
   */
  async getAllEmployees(
    branchId: number, 
    pageNumber: number, 
    pageSize: number
  ): Promise<EmployeeListResponse> {
    const url = endpoints.employees.getAll(branchId, pageNumber, pageSize)
    const response = await apiClient.get<ApiResponse<EmployeeListResponse> | EmployeeListResponse>(url)
    // Handle both wrapped and direct responses
    if ('data' in response.data && response.data.data && 'success' in response.data) {
      return (response.data as ApiResponse<EmployeeListResponse>).data
    }
    // API returns direct response with structure: { totalRecords, pageNumber, pageSize, data }
    return response.data as EmployeeListResponse
  }

  /**
   * Get single employee by ID
   */
  async getEmployeeById(branchId: number, id: number): Promise<Employee> {
    const url = endpoints.employees.getById(branchId, id)
    const response = await apiClient.get<ApiResponse<Employee> | Employee | Record<string, unknown>>(url)
    const raw = response.data as Record<string, unknown> | Employee
    // Wrapped: { data: Employee } or { data: { data: Employee } } (some APIs)
    if (raw && typeof raw === 'object' && 'data' in raw && raw.data && typeof raw.data === 'object') {
      const inner = raw.data as Record<string, unknown>
      if ('data' in inner && inner.data && typeof inner.data === 'object') {
        return inner.data as Employee
      }
      return raw.data as Employee
    }
    return raw as Employee
  }

  /**
   * Create new employee (multipart/form-data).
   * Uses fetch so request matches Swagger exactly: no axios transforms, browser sets Content-Type with boundary.
   */
  async saveEmployee(formData: FormData): Promise<ApiResponse<Employee>> {
    const baseUrl = config.api.baseUrl?.replace(/\/$/, '') || ''
    const path = endpoints.employees.save
    const url = `${baseUrl}${path.startsWith('/') ? path : '/' + path}`
    const { token } = useAuthStore.getState()
    const headers: HeadersInit = {
      Accept: 'application/json',
    }
    if (token) headers['Authorization'] = `Bearer ${token}`

    // eslint-disable-next-line no-console
    console.groupCollapsed('[Employee API] POST SaveEmployee')
    // eslint-disable-next-line no-console
    console.log('url', url)
    // eslint-disable-next-line no-console
    console.log('payload (multipart fields)', formDataToDebugRecord(formData))

    const res = await fetch(url, {
      method: 'POST',
      body: formData,
      headers,
    })
    const body = await res.json().catch(() => ({}))

    // eslint-disable-next-line no-console
    console.log('response.httpStatus', res.status, res.statusText)
    // eslint-disable-next-line no-console
    console.log('response.body (parsed JSON)', body)
    // eslint-disable-next-line no-console
    console.groupEnd()

    if (!res.ok) {
      if (employeeSaveResponseLooksSuccessful(body)) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Employee API] SaveEmployee HTTP',
          res.status,
          'but response body indicates success — treating as OK (check backend for spurious 500).',
        )
        return body as ApiResponse<Employee>
      }
      // Backend sometimes persists then returns HTTP 500 with empty/non-JSON body — still show success in UI.
      if (res.status === 500) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Employee API] SaveEmployee HTTP 500 — treating as success (data often saved; fix backend status).',
        )
        return (
          body && typeof body === 'object' && Object.keys(body as Record<string, unknown>).length > 0
            ? (body as ApiResponse<Employee>)
            : ({ success: true } as ApiResponse<Employee>)
        )
      }
      // eslint-disable-next-line no-console
      console.error('[Employee API] SaveEmployee failed — see response.body above; status', res.status)
      throw Object.assign(new Error(body?.message ?? body?.Message ?? `Save failed (${res.status})`), {
        response: { status: res.status, data: body },
      })
    }
    return body as ApiResponse<Employee>
  }

  /**
   * Update existing employee (multipart/form-data).
   * Do not set Content-Type: apiClient interceptor removes it for FormData so browser sets boundary.
   */
  async updateEmployee(formData: FormData): Promise<ApiResponse<Employee>> {
    const path = endpoints.employees.update
    // eslint-disable-next-line no-console
    console.groupCollapsed('[Employee API] PUT UpdateEmployee')
    // eslint-disable-next-line no-console
    console.log('path', path)
    // eslint-disable-next-line no-console
    console.log('payload (multipart fields)', formDataToDebugRecord(formData))
    try {
      const response = await apiClient.put<ApiResponse<Employee>>(path, formData, {
        skipGlobal500Swal: true,
      } as Record<string, unknown>)
      // eslint-disable-next-line no-console
      console.log('response.httpStatus', response.status, response.statusText)
      // eslint-disable-next-line no-console
      console.log('response.data (full)', response.data)
      // eslint-disable-next-line no-console
      console.groupEnd()
      return response.data
    } catch (err: any) {
      const status = err?.response?.status
      const data = err?.response?.data
      // eslint-disable-next-line no-console
      console.log('response.httpStatus', status)
      // eslint-disable-next-line no-console
      console.log('response.data (full)', data)
      // eslint-disable-next-line no-console
      console.groupEnd()
      if (status >= 400 && employeeSaveResponseLooksSuccessful(data)) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Employee API] UpdateEmployee HTTP',
          status,
          'but response body indicates success — treating as OK (check backend for spurious 4xx/5xx).',
        )
        return data as ApiResponse<Employee>
      }
      if (status === 500) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Employee API] UpdateEmployee HTTP 500 — treating as success (data often saved; fix backend status).',
        )
        return (
          data && typeof data === 'object' && Object.keys(data as Record<string, unknown>).length > 0
            ? (data as ApiResponse<Employee>)
            : ({ success: true } as ApiResponse<Employee>)
        )
      }
      throw err
    }
  }

  /**
   * Delete employee by ID
   */
  async deleteEmployee(id: number): Promise<void> {
    const url = endpoints.employees.delete(id)
    await apiClient.delete(url)
  }

  /**
   * Get gender lookups
   */
  async getGenderLookups(): Promise<GenderLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.gender)
      console.log('Gender lookup response:', response.data)
      
      // Handle different response structures
      let data = response.data
      
      // If wrapped in ApiResponse
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      // If direct array
      if (Array.isArray(data)) {
        // Map to simplified structure - backend handles isActive filtering
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: GenderLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching gender lookups:', error)
      return []
    }
  }

  /**
   * Get department lookups
   */
  async getDepartmentLookups(): Promise<DepartmentLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.departments)
      console.log('Department lookup response:', response.data)
      
      let data = response.data
      
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      if (Array.isArray(data)) {
        // Map to simplified structure - backend handles isActive filtering
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: DepartmentLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching department lookups:', error)
      return []
    }
  }

  /**
   * Get country lookups
   */
  async getCountryLookups(): Promise<CountryLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.countries)
      console.log('Country lookup response:', response.data)
      
      let data = response.data
      
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      if (Array.isArray(data)) {
        // Map to simplified structure - backend handles isActive filtering
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: CountryLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching country lookups:', error)
      return []
    }
  }

  /**
   * Get city lookups
   */
  async getCityLookups(): Promise<CityLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.cities)
      console.log('City lookup response:', response.data)
      
      let data = response.data
      
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      if (Array.isArray(data)) {
        // Map to simplified structure - backend handles isActive filtering
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: CityLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching city lookups:', error)
      return []
    }
  }

  /**
   * Get profession in iqama lookups
   */
  async getProfessionIqamaLookups(): Promise<ProfessionIqamaLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.professionIqama)
      console.log('Profession iqama lookup response:', response.data)
      
      let data = response.data
      
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      if (Array.isArray(data)) {
        // Map to simplified structure - backend handles isActive filtering
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: ProfessionIqamaLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching profession iqama lookups:', error)
      return []
    }
  }

  /**
   * Get notice period lookups
   */
  async getNoticePeriodLookups(): Promise<NoticePeriodLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.noticePeriod)
      console.log('Notice period lookup response:', response.data)
      
      let data = response.data
      
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      if (Array.isArray(data)) {
        // Map to simplified structure - backend handles isActive filtering
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: NoticePeriodLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching notice period lookups:', error)
      return []
    }
  }

  /**
   * Get marital status lookups
   */
  async getMaritalStatusLookups(): Promise<MaritalStatusLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.maritalStatus)
      console.log('Marital status lookup response:', response.data)
      
      let data = response.data
      
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      if (Array.isArray(data)) {
        // Map to simplified structure - backend handles isActive filtering
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: MaritalStatusLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching marital status lookups:', error)
      return []
    }
  }

  /**
   * Get job lookups
   */
  async getJobLookups(): Promise<JobLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.jobs)
      console.log('Job lookup response:', response.data)
      
      let data = response.data
      
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      
      if (Array.isArray(data)) {
        // Map to simplified structure - backend handles isActive filtering
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: JobLookup) => item.id > 0)
      }
      
      return []
    } catch (error) {
      console.error('Error fetching job lookups:', error)
      return []
    }
  }

  /**
   * Get employee status lookups (e.g. Active, Inactive).
   * Falls back to static options if API is not available.
   */
  async getStatusLookups(): Promise<StatusLookup[]> {
    try {
      const response = await apiClient.get<any>(endpoints.employeeLookups.status)
      let data = response.data
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        data = data.data
      }
      if (Array.isArray(data)) {
        return data.map((item: any) => ({
          id: item.id || 0,
          lookupNameEn: item.lookupNameEn || '',
          lookupNameAr: item.lookupNameAr || '',
        })).filter((item: StatusLookup) => item.id > 0)
      }
      return []
    } catch {
      // Fallback when backend has no GetStatusLookups
      return [
        { id: 1, lookupNameEn: 'Active', lookupNameAr: 'نشط' },
        { id: 0, lookupNameEn: 'Inactive', lookupNameAr: 'غير نشط' },
      ]
    }
  }
}

export const employeesService = new EmployeesService()
